import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, orderBy, getDocs, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useNavigate } from 'react-router-dom';

const JobSeekerApplications = () => {
  const { currentUser } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const [providerNames, setProviderNames] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;
    const fetchApplications = async () => {
      setLoading(true);
      setError(null);
      try {
        const q = query(collection(db, 'applications'), where('jobSeekerUid', '==', currentUser.uid), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        const apps = snapshot.docs.map(docSnap => {
          const appData = docSnap.data();
          return {
            id: docSnap.id,
            jobTitle: appData.jobTitle || 'N/A',
            providerUid: appData.providerUid || 'N/A',
            parentUid: appData.parentUid || 'N/A',
            appliedAt: appData.createdAt,
            jobId: appData.jobId,
            status: appData.status || 'N/A',
          };
        });
        setApplications(apps);
        // Fetch names for 'Posted By' (provider or parent)
        const uniqueUids = [...new Set(apps.map(app => (app.providerUid && app.providerUid !== 'N/A') ? app.providerUid : app.parentUid).filter(uid => uid && uid !== 'N/A'))];
        const namesMap = {};
        for (const app of apps) {
          const isProvider = app.providerUid && app.providerUid !== 'N/A';
          const displayUid = isProvider ? app.providerUid : app.parentUid;
          if (!displayUid || namesMap[displayUid]) continue;
          try {
            let userDoc = null;
            if (isProvider) {
              userDoc = await getDoc(doc(db, 'providers', app.providerUid));
            } else {
              userDoc = await getDoc(doc(db, 'parents', app.parentUid));
            }
            if (!userDoc || !userDoc.exists()) {
              userDoc = await getDoc(doc(db, 'users', displayUid));
            }
            if (userDoc && userDoc.exists()) {
              const data = userDoc.data();
              namesMap[displayUid] = data.displayName || data.name || data.email || displayUid;
            } else {
              namesMap[displayUid] = displayUid;
            }
          } catch {
            namesMap[displayUid] = displayUid;
          }
        }
        setProviderNames(namesMap);
      } catch (err) {
        setError('Failed to load applications.');
      } finally {
        setLoading(false);
      }
    };
    fetchApplications();
  }, [currentUser]);

  const handleWithdraw = async (applicationId) => {
    if (!window.confirm('Are you sure you want to withdraw this application?')) return;
    setWithdrawing(applicationId);
    try {
      await deleteDoc(doc(db, 'applications', applicationId));
      setApplications(prev => prev.filter(app => app.id !== applicationId));
    } catch (err) {
      alert('Failed to withdraw application: ' + err.message);
    } finally {
      setWithdrawing(null);
    }
  };

  return (
    <div className="job-applications">
      <h1>Jobs Applied</h1>
      {error && <div className="error-message">{error}</div>}
      <div className="applications-list">
        {loading ? (
          <div className="loading-container"><p>Loading applications...</p></div>
        ) : applications.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>Job Title</th>
                <th>Posted By</th>
                <th>Applied</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.map(job => {
                const isProvider = !!(job.providerUid && job.providerUid !== 'N/A' && job.providerUid !== '' && job.providerUid !== null && job.providerUid !== undefined);
                const displayUid = isProvider ? job.providerUid : job.parentUid;
                const profileType = isProvider ? 'provider' : 'parent';
                const viewLabel = isProvider ? 'View Provider' : 'View Parent';
                return (
                  <tr key={job.id} className="application-row">
                    <td>
                      <button className="btn btn-link btn-sm" onClick={() => navigate(`/jobseeker/jobs/${job.jobId}`)}>
                        {job.jobTitle}
                      </button>
                    </td>
                    <td>
                      <button 
                        className="btn btn-link btn-sm" 
                        onClick={() => navigate(`/${profileType}/${displayUid}`)}
                      >
                        {providerNames[displayUid] || displayUid}
                      </button>
                    </td>
                    <td>{job.appliedAt && (job.appliedAt.seconds ? new Date(job.appliedAt.seconds * 1000).toLocaleDateString() : new Date(job.appliedAt).toLocaleDateString())}</td>
                    <td>
                      <div className="table-actions">
                        <button className="btn btn-outline btn-sm" onClick={() => navigate(`/jobseeker/jobs/${job.jobId}`)}>View Job</button>
                        <button 
                          className="btn btn-outline btn-sm" 
                          onClick={() => navigate(`/${profileType}/${displayUid}`)}
                        >
                          {viewLabel}
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleWithdraw(job.id)} disabled={withdrawing === job.id}>
                          {withdrawing === job.id ? 'Withdrawing...' : 'Withdraw'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="empty-state"><p>No jobs applied yet.</p></div>
        )}
      </div>
    </div>
  );
};

export default JobSeekerApplications; 